import { KIT_STORE_TYPE, DARK_THEME } from "@constants/store";
import { createSelector } from "@reduxjs/toolkit";
import { PRICELESS_CURRENCY_OBJ } from "@stadium/constants/currency";

const emptyArr = [];
const emptyObj = {};

export const getStore = ({ store }) => store;
export const getStoreBalance = ({ store }) => +store?.balance || 0;
export const getStoreInitBalance = ({ store }) => +store?.initBalance || 0; // same as getStoreBalance except for open orders
export const getStoreExternalId = ({ store }) => store?.externalId;
export const getStoreIsOrderFetching = ({ store }) => store?.isStoreOrderFetching;
export const getStoreIsOrderFetched = ({ store }) => store?.isStoreOrderFetched;
export const getStoreIsAccessibilityFetched = ({ store }) => store?.isStoreAccessibilityFetched;
export const getStoreCloneId = ({ store }) => +store?.cloneId;
export const getStoreID = ({ store }) => +store?.catalogStoreId || +store?.id;
export const getShopStoreId = ({ store }) => +store?.id;
export const getStoreNumber = ({ store }) => store?.catalogStoreNumber || store?.number;
export const getStoreLogo = ({ store }) => store?.logo;
export const getStoreLogoType = ({ store }) => store?.logoType;
export const getStoreCatalogLogo = ({ store }) => store?.catalogLogoUrl;
export const getStoreCatalogLogoFileName = ({ store }) => store?.catalogLogoFileName;
export const getStoreCatalogLogoId = ({ store }) => store?.catalogLogoId;
export const getStoreCustomFooterText = ({ store }) => store?.storeDetail?.footerText;
export const getIsShopPrivate = ({ store }) => store?.private;
export const getStoreBlacklistedCountries = ({ store }) =>
  store?.storeDetail?.blacklistedCountries || emptyArr;

export const getWorkspaceUuidFromStore = ({ store }) => store?.workspaceId;

export const getStoreMedleyPriceOptions = ({ store }) => store?.config?.medleyPrices;
export const getStoreUsdMedleyPriceOptions = ({ store }) => store?.config?.usdMedleyPrices;
export const getStoreStcMedleyPriceOptions = ({ store }) => store?.config?.stcMedleyPrices;
export const getStoreName = ({ store }) => store?.name;
export const getStoreSlug = ({ store }) => store?.code;
export const getStoreWithWorkspaceSlug = ({ store }) => store?.slug;
export const getAppTheme = ({ store }) => store?.theme;
export const getStoreTopBanner = ({ store }) => store?.topBanner;
export const getStoreMobileTopBanner = ({ store }) => store?.mobileTopBanner;
export const getStoreBanner = ({ store }) => store?.banner;
export const getStoreCurrencyCode = ({ store }) =>
  store?.dummyStore?.currency || store?.config?.currencyCode;
export const getStoreCurrencyName = ({ store }) => store?.config?.currencyName;
export const getStoreCurrencyPrefix = ({ store }) => store?.config?.currencyPrefix;
export const getStoreCurrencySuffix = ({ store }) => store?.config?.currencySuffix;
export const getStoreParentId = ({ store }) => +store?.parentId;
export const getStoreSyncedToES = ({ store }) => store?.syncedToEs;
export const getStoreHideYouMightAlsoLike = ({ store }) => !store?.showYouMightAlsoLike;
export const getStoreHideMoreFromThisBrand = ({ store }) => !store?.showMoreFromThisBrand;
export const getStoreLongDescription = ({ store }) => store?.storeDetail?.longDescription;
export const getStoreThankyouTitle = ({ store }) => store?.storeDetail?.thankYouTitle;
export const getStoreThankyouMessage = ({ store }) => store?.storeDetail?.thankYouMessage;
export const getStoreHideThankYouMessagePlaceholder = ({ store }) =>
  store?.storeDetail?.hideThankYouMessagePlaceholder || false;
export const getStoreHideThankYouMessagePrompts = ({ store }) =>
  store?.storeDetail?.hideThankYouMessagePrompts || false;
export const getStoreType = ({ store }) => store?.config?.type;
export const getStoreFeUrl = ({ store }) => store?.feUrl;
export const getStoreUpcharge = ({ store }) => store?.config?.upcharge || 1;
export const getParentStoreNumber = ({ store }) => store?.parent?.number;
export const getIsLockerShop = ({ store }) =>
  Boolean(store?.isLockerShop) || Boolean(store?.actAsLockerShop);
export const getAllowCorporateBilling = createSelector(
  getStore,
  getIsLockerShop,
  (store, isLockerShop) => isLockerShop || store?.storeDetail?.allowCorporateBilling || false,
);
export const getIsLegacyLockerShop = ({ store }) => Boolean(store?.isLockerShop);
export const getIsSinglePageShop = ({ store }) => store?.storeDetail?.hideMenuBar;
export const getStoreCacheKey = ({ store }) => store?.cacheKey;
export const getDisplayWarehouseAddress = ({ store }) => store?.displayWarehouseAddress;
export const getDesignedStoreIds = ({ store }) => store?.designedStoreIds || emptyArr;

export const getStoreAccessibleThroughOrders = ({ store }) => store?.storeAccessibleThroughOrders;
export const getStoreIsWhitelabeled = ({ store }) => store?.membership?.whitelabel;
export const getIsFeaturedStore = ({ store }) => store?.featured; //Shops which are marked as "Customer Case Study"
export const getIsFiltersOnLeft = ({ store }) => store?.storeDetail?.filterLocation !== "top";
export const getLockerShopId = ({ store }) => store?.lockerShopId;
export const getLockerShopKey = ({ store }) => store?.lockerShopKey;
export const getIsHideBulkPricing = ({ store }) => store?.hideBulkPricing || false;

export const getCommonElasticSearchStoreVariables = createSelector(
  getStoreID,
  getStoreUpcharge,
  getIsHideBulkPricing,
  (storeId, storeUpcharge, isHideBulkPricing) => {
    return {
      currentStoreId: +storeId,
      storeUpcharge,
      excludeTierPricing: isHideBulkPricing,
    };
  },
);

export const getIsStoreAccessible = ({ store }) => store?.accessible;

export const getIsBundleShop = ({ store }) => store?.isKitShop;

export const getIsKitStore = ({ store }) => store?.shopType === KIT_STORE_TYPE;
export const getIsOrganizerShareAdded = ({ store }) => store?.isOrganizerShareAdded || false;
export const getPastleUrl = ({ store }) => store?.storeDetail?.pastelUrl || "";
export const getStoreAnalytics = ({ store }) => store?.storeDetail?.analytics || emptyObj;
export const getStoreOwnersEmails = ({ store }) =>
  store?.storeOwners?.map(({ email }) => email) || emptyArr;
export const getStoreIsFeaturedMenuHidden = ({ store }) =>
  store?.storeDetail?.featuredMenuHidden || false;
export const getIsStorePointsMigrationAllowed = ({ store }) =>
  store?.pointsMigrationAllowed || false;
export const getIsSendToManyAllowed = ({ store }) => store?.storeDetail?.sendToManyEnabled;
export const getIsBillingNoteRequired = ({ store }) =>
  store?.storeDetail?.billingNoteRequired || false;
export const getAdditionalBillingNote = ({ store }) =>
  store?.storeDetail?.additionalBillingNote || "";

export const getStoreAccessibilityEnabled = ({ store }) => store?.accessibilityEnabled || false;
export const getStoreCurrencyDetails = ({ store }) => ({
  storeCurrencyName: store?.config?.currencyName,
  storeCurrencyCode: store?.config?.currencyCode,
  storeNumber: store?.number,
  storeBalance: store?.balance || 0,
  storeCurrencyPrefix: store?.config?.currencyPrefix,
  storeCurrencySuffix: store?.config?.currencySuffix,
});

export const getStorePriceShouldRoundOff = ({ store }) => store?.enableCeilPrices;
export const getDummyStoreCurreny = ({ store }) => store?.dummyStore?.currency;
export const getDummyStoreCreateAutomatedCatalog = ({ store }) =>
  store?.dummyStore?.createAutomatedCatalog || false;
export const getDummyStoreDetails = ({ store }) => store?.dummyStore || emptyObj;
export const getDummyStoreId = ({ store }) => store?.dummyStore?.id;
export const getDummyStoreTaxonIds = ({ store }) => store?.taxonIds;
export const getIsPricelessShop = ({ store }) =>
  store?.dummyStore?.currency === PRICELESS_CURRENCY_OBJ.currencyKey ||
  store?.currencyType === PRICELESS_CURRENCY_OBJ.currencyKey;

export const selectStoreItemsPriceRange = createSelector(getStore, (store) => ({
  minPrice: store?.minPrice,
  maxPrice: store?.maxPrice,
}));

export const getAllowSendPointsFromStorefront = ({ store }) =>
  store?.storeDetail?.allowSendPointsFromStorefront || false;

export const getDummyStorePriceRange = ({ store }) => ({
  minPrice: store?.dummyStore?.minPrice,
  maxPrice: store?.dummyStore?.maxPrice,
});
export const getIsStoreDarkTheme = ({ store }) => store?.storeDetail?.navBarTheme === DARK_THEME;

export const getIsBillingNoteOnSelfOrder = ({ store }) =>
  store?.storeDetail?.billingNoteOnSelfOrder || false;

export const getIsBookACallEnabled = ({ store }) => store?.storeDetail?.showBookACall || false;
export const getIsHideCountryFlag = ({ store }) => store?.storeDetail?.hideCountryFlags || false;
export const getShowBreadcrumbsOnProductListing = ({ store }) =>
  store?.showBreadcrumbsOnProductListing || false;
export const getShowBannerOnProductListing = ({ store }) =>
  store?.showBannerOnProductListing || false;
export const getStoreShowCategoryImages = ({ store }) =>
  store?.storeDetail?.showCategoryImages || false;

export const getIsNewRouteEnabled = ({ store }) => store?.storeDetail?.newRoutesEnabled || false;
